import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import Paragraph from '../../components/Paragraph/Paragraph';
import { useMetalStreamData } from '../../hooks/useMetalStreamData';
import MetalStreamAllocateForm from '../../components/Forms/MetalStreamAllocateForm';
import { metalStreamDataHelper } from '../../util/helpers/metalStreamDataHelper';
import useMetalStreamSelectedProducts from '../../hooks/useMetalStreamSelectedProducts';
import { fetchBankAccounts } from '../../store/actions/bankAccount/bankAccountActions';
import { selectBankAccounts } from '../../store/selectors/bankAccountSelectors';
import useApplicationUid from '../../hooks/useApplicationUid';
import MetalStreamAmountForm from '../../components/Forms/MetalStreamAmountForm';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import SelectFundingMethod from '../../components/SelectFundingMethod/SelectFundingMethod';
import Modal from '../../components/Modal';
import SelectionContainer from '../../components/SelectionContainer';
import ReviewData from '../../components/Review/ReviewData';
import MetalStreamForm from '../../components/MetalStreamForm';
import themeColors from '../../assets/styles/themeColors';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 0 1.5rem 0 1.5rem;

  @media only screen and (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 0.5rem 0.5rem 0;
  }
`;

const ReviewDataContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  padding: 1.5rem 1.5rem 0 1.5rem;

  @media only screen and (max-width: 900px) {
    padding: 1.5rem 0.5rem 0;
  }
`;

const MetalStreamReviewData = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { ApplicationUid } = useApplicationUid();
  const [isAllocationFormOpen, setIsAllocationFormOpen] = useState(false);
  const [isGoldFormOpen, setIsGoldFormOpen] = useState(false);
  const [isSilverFormOpen, setIsSilverFormOpen] = useState(false);
  const [isFundingTypeFormOpen, setIsFundingTypeFormOpen] = useState(false);
  const [isAmountFormOpen, setIsAmountFormOpen] = useState(false);

  const [bankAccount, setBankAccount] = useState({});

  const {
    silverMetalReviewProps,
    goldMetalReviewProps,
  } = useMetalStreamSelectedProducts();
  const { MetalStreamData } = useMetalStreamData();
  const goldPercentage = metalStreamDataHelper(
    MetalStreamData,
    'GoldPercentage',
  );

  const silverPercentage = metalStreamDataHelper(
    MetalStreamData,
    'SilverPercentage',
  );

  const goldProductCode = metalStreamDataHelper(
    MetalStreamData,
    'SymbolCodeGold',
  );

  const silverProductCode = metalStreamDataHelper(
    MetalStreamData,
    'SymbolCodeSilver',
  );

  const bankAccounts = useSelector(selectBankAccounts);

  const Amount = metalStreamDataHelper(MetalStreamData, 'Amount');
  const BankAccountUid = metalStreamDataHelper(
    MetalStreamData,
    'BankAccountUid',
  );
  const UseAccountBalance = metalStreamDataHelper(
    MetalStreamData,
    'UseAccountBalance',
  );

  const onAllocationModalClose = () => {
    setIsAllocationFormOpen(false);
    if (!silverProductCode) {
      setIsSilverFormOpen(true);
    }
    if (!goldProductCode) {
      setIsGoldFormOpen(true);
    }
  };

  const isSilverProductVisible = silverPercentage !== 0 && silverProductCode;
  const isGoldProductVisible = goldPercentage !== 0 && goldProductCode;

  useEffect(() => {
    if (BankAccountUid) {
      if (BankAccountUid && !isEmpty(bankAccounts)) {
        const selectedBankAccount = bankAccounts.find(
          (bank) => bank.BankAccountUid === BankAccountUid,
        );
        if (selectedBankAccount) {
          setBankAccount(selectedBankAccount);
        }
      }
    }
  }, [BankAccountUid, bankAccounts]);

  useEffect(() => {
    if (!UseAccountBalance) {
      dispatch(
        fetchBankAccounts({
          ApplicationUid,
        }),
      );
    }
  }, [ApplicationUid, UseAccountBalance, BankAccountUid, dispatch]);

  return (
    <>
      <Grid>
        <ReviewData
          title={t('metalstream.depositAmount')}
          onEdit={() => setIsAmountFormOpen('isAmountOpen', true)}
        >
          <Paragraph>
            <strong>{formatMoneyNumeral(Amount)}</strong>{' '}
            {t('standardReview.recurringPayment')}
          </Paragraph>
        </ReviewData>
        <ReviewData
          title={t('metalstream.depositType')}
          onEdit={() => setIsFundingTypeFormOpen(true)}
        >
          {UseAccountBalance ? (
            <Paragraph>
              {t('standardReview.fundTypes.accountBalance')}
            </Paragraph>
          ) : (
            <Paragraph>
              {t('standardReview.fundTypes.plaid', {
                bankName: bankAccount?.BankName,
                last4digits: bankAccount?.AccountNumber?.substr(
                  (bankAccount?.AccountNumber).length - 4,
                ),
              })}
            </Paragraph>
          )}
        </ReviewData>
        <ReviewData
          title={t('metalstream.reviewAllocation')}
          onEdit={() => setIsAllocationFormOpen(true)}
        >
          <Paragraph color={themeColors.colorTextXLLight}>
            {goldPercentage !== 0 &&
              ` ${t('metalstream.reviewGold')} ${goldPercentage} % /`}
            {silverPercentage !== 0 &&
              `${t('metalstream.reviewSilver')} ${silverPercentage}% `}
          </Paragraph>
        </ReviewData>
      </Grid>

      <ReviewDataContainer>
        <Paragraph mb={10} fontWeight={700}>
          {t('metalstream.reviewMetal')}:
        </Paragraph>
        {isGoldProductVisible && (
          <SelectionContainer
            {...goldMetalReviewProps}
            onClick={() => setIsGoldFormOpen(true)}
          />
        )}
        {isSilverProductVisible && (
          <SelectionContainer
            {...silverMetalReviewProps}
            onClick={() => setIsSilverFormOpen(true)}
            showDivider={false}
          />
        )}
      </ReviewDataContainer>
      <Modal
        isOpen={isFundingTypeFormOpen}
        onClose={() => setIsFundingTypeFormOpen(false)}
        variant="lg"
      >
        <SelectFundingMethod
          onModalClose={() => setIsFundingTypeFormOpen(false)}
        />
      </Modal>
      <Modal
        isOpen={isAmountFormOpen}
        onClose={() => setIsAmountFormOpen(false)}
        label={t('metalstream.investTitle')}
      >
        <MetalStreamAmountForm
          onModalClose={() => setIsAmountFormOpen(false)}
        />
      </Modal>
      <Modal
        isOpen={isSilverFormOpen}
        onClose={() => setIsSilverFormOpen(false)}
        label={t('metalstream.reviewSilver')}
      >
        <MetalStreamForm
          shouldNavigate={false}
          productType="Silver"
          onModalClose={() => setIsSilverFormOpen(false)}
        />
      </Modal>
      <Modal
        isOpen={isAllocationFormOpen}
        onClose={() => setIsAllocationFormOpen(false)}
        label={t('metalstream.allocation')}
      >
        <MetalStreamAllocateForm
          shouldNavigate={false}
          onModalClose={onAllocationModalClose}
        />
      </Modal>
      <Modal
        isOpen={isGoldFormOpen}
        onClose={() => setIsGoldFormOpen(false)}
        label={t('metalstream.reviewGold')}
      >
        <MetalStreamForm
          shouldNavigate={false}
          productType="Gold"
          onModalClose={() => setIsGoldFormOpen(false)}
        />
      </Modal>
    </>
  );
};

export default MetalStreamReviewData;
